import React, { useEffect, useState } from 'react';
import {
    Dialog,
    Button,
    InputLabel,
    IconButton,
    DialogTitle,
    DialogContent,
} from '@mui/material';

import Alert from '@mui/material/Alert';

import { SET_ALERT } from 'context/actions/system';

import { useDispatch } from 'react-redux';
import { validateEmail } from 'utils/functions';

// components
import CustomTextBox from 'components/CustomTextBox';
import { X, Upload } from 'react-feather';

import useWindowSize from 'utils/useWindowSize';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';

const EditProfileCompose = ({
    open,
    handleClose,
    onSave,
    isEdit,
    editItem,
}) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [profileImageFile, setProfileImageFile] = useState(null);
    const [profileImage, setProfileImage] = useState(null);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [mobile, setMobile] = useState('');
    const [mail, setMail] = useState('');
    const [password, setPassword] = useState('');
    const [passwordRepeat, setPasswordRepeat] = useState('');
    const [refresh, setRefesh] = useState(false);

    const { sizes } = useWindowSize();

    const [errors, setErrors] = useState({
        firstName: false,
        lastName: false,
        mobile: false,
        mail: false,
        password: false,
        passwordRepeat: false,
    });

    useEffect(() => {
        if (isEdit && editItem) {
            // засах горим — утгуудыг populate хийх
            setProfileImage(editItem.photo || null);
            setFirstName(editItem.firstName || '');
            setLastName(editItem.lastName || '');
            setMobile(editItem.mobile || '');
            setMail(editItem.email || '');
        } else {
            // шинээр бүртгэх горим — утгуудыг reset хийх
            setProfileImage(null);
            setFirstName('');
            setLastName('');
            setMobile('');
            setMail('');
        }
    }, [editItem, isEdit, refresh]);

    const handleImageUpload = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setProfileImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setProfileImage(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const onChangeFirstName = (value) => setFirstName(value);
    const onChangeLastName = (value) => setLastName(value);
    const onChangeMobile = (value) => setMobile(value);
    const onChangeMail = (value) => setMail(value);
    const onChangePassword = (value) => setPassword(value);
    const onChangePasswordRepeat = (value) => setPasswordRepeat(value);

    const closePopUp = () => {
        handleClose(false);
        if (isEdit) {
            setRefesh(!refresh);
        }
    };

    const handleSave = (e) => {
        e.preventDefault();
        let hasError = false;

        if (firstName.length === 0) {
            hasError = true;
            errors.firstName = true;
        } else {
            errors.firstName = false;
        }

        if (lastName.length === 0) {
            hasError = true;
            errors.lastName = true;
        } else {
            errors.lastName = false;
        }

        if (mobile.length === 0) {
            hasError = true;
            errors.mobile = true;
        } else {
            errors.mobile = false;
        }

        if (mail.length === 0) {
            hasError = true;
            errors.mail = true;
        } else {
            errors.mail = false;
        }

        if (!editItem && password.length === 0) {
            hasError = true;
            errors.password = true;
        } else {
            errors.password = false;
        }

        if (!editItem && passwordRepeat.length === 0) {
            hasError = true;
            errors.passwordRepeat = true;
        } else {
            errors.passwordRepeat = false;
        }

        setErrors({ ...errors });

        if (hasError) {
            dispatch({
                type: SET_ALERT,
                data: {
                    isShow: true,
                    type: 'warning',
                    message: t('warning.fill_all_info'),
                },
            });
            return;
        }

        const isValidate = validateEmail(mail);

        if (!isValidate) {
            dispatch({
                type: SET_ALERT,
                data: {
                    isShow: true,
                    type: 'warning',
                    message: t('title.not_validate_mail'),
                },
            });
            return;
        }

        if (password !== passwordRepeat) {
            dispatch({
                type: SET_ALERT,
                data: {
                    isShow: true,
                    type: 'warning',
                    message: t('warning.not_match_password'),
                },
            });
            return;
        }

        onSave(
            {
                firstName,
                lastName,
                mobile,
                mail,
                password,
                profileImageFile,
                profileImage,
            },
            clearFields,
        );
    };

    const clearFields = () => {
        setFirstName('');
        setLastName('');
        setMobile('');
        setMail('');
        setPassword('');
        setPasswordRepeat('');
        setProfileImage(null);
        setProfileImageFile(null);
    };

    return (
        <Dialog
            onClose={closePopUp}
            open={open}
            fullScreen={sizes.sm}
            maxWidth="sm" // max өргөн
            fullWidth // дэлгэцийн өргөнтэй нийцүүлэх
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                {isEdit ? t('button.profile_edit') : t('title.register')}
                <IconButton onClick={closePopUp}>
                    <X />
                </IconButton>
            </DialogTitle>
            <DialogContent className="space-y-4 mx-1 md:mx-5">
                {/* Profile Picture Upload */}
                <div className="flex flex-col items-center gap-2">
                    <InputLabel>{t('title.profile_image')}</InputLabel>
                    <div className="relative">
                        {profileImage ? (
                            <>
                                <div className="relative w-24 h-24 rounded-full overflow-hidden border-2 border-primary">
                                    <img
                                        src={profileImage}
                                        alt="Profile"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setProfileImageFile(null);
                                        setProfileImage(null);
                                    }}
                                    className="absolute top-1 right-1 bg-destructive text-destructive-foreground rounded-full p-1 hover:bg-destructive/90"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </>
                        ) : (
                            <label className="w-24 h-24 rounded-full border-2 border-dashed border-muted-foreground flex items-center justify-center cursor-pointer hover:border-primary-main transition-colors">
                                <Upload className="w-8 h-8 text-muted-foreground" />
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                    className="hidden"
                                />
                            </label>
                        )}
                    </div>
                </div>

                {/* Last Name */}
                <div className="space-y-2">
                    <CustomTextBox
                        label={t('title.last_name')}
                        type="text"
                        placeholder={t('warning.please_fill_last_name')}
                        required
                        value={lastName}
                        error={errors.lastName}
                        onChange={onChangeLastName}
                    />
                </div>

                {/* First Name */}
                <div className="space-y-2">
                    <CustomTextBox
                        label={t('title.first_name')}
                        type="text"
                        placeholder={t('warning.please_fill_first_name')}
                        required
                        value={firstName}
                        error={errors.firstName}
                        onChange={onChangeFirstName}
                    />
                </div>

                {/* Phone Number */}
                <div className="space-y-2">
                    <CustomTextBox
                        label={t('title.mobile_number')}
                        type="tel"
                        placeholder="99999999"
                        required
                        value={mobile}
                        error={errors.mobile}
                        onChange={onChangeMobile}
                    />
                </div>

                {/* Email */}
                <div className="space-y-2">
                    <CustomTextBox
                        label={t('title.mail_address')}
                        type="email"
                        placeholder="example@email.com"
                        required
                        value={mail}
                        error={errors.mail}
                        onChange={onChangeMail}
                    />
                </div>

                {/* Password */}

                {!isEdit && (
                    <>
                        <div className="space-y-2">
                            <CustomTextBox
                                label={t('title.password')}
                                type="password"
                                placeholder={t('warning.please_fill_password')}
                                required
                                value={password}
                                error={errors.password}
                                onChange={onChangePassword}
                            />
                        </div>

                        {/* Confirm Password */}
                        <div className="space-y-2">
                            <CustomTextBox
                                label={t('title.password_repeat')}
                                type="password"
                                placeholder={t(
                                    'warning.please_fill_password_repeat',
                                )}
                                required
                                value={passwordRepeat}
                                error={errors.passwordRepeat}
                                onChange={onChangePasswordRepeat}
                            />
                        </div>
                    </>
                )}

                <Button
                    variant="contained"
                    type="submit"
                    className="w-full !rounded-lg !my-10"
                    size="lg"
                    onClick={handleSave}
                >
                    {t('title.save')}
                </Button>
            </DialogContent>
        </Dialog>
    );
};

export default EditProfileCompose;
